import { USER_ID } from "@/config/constants";
import { ISchoolYear } from "@/services/schoolCalendar";

// 学年信息的本地存储键
const SCHOOL_YEAR_STORAGE_KEY = "current_school_year";
const SCHOOL_YEAR_STORAGE_PREFIX = `${SCHOOL_YEAR_STORAGE_KEY}_`;

const getScopedStorageKey = (): string => {
  try {
    const userId = localStorage.getItem(USER_ID);
    if (userId) {
      return `${SCHOOL_YEAR_STORAGE_PREFIX}${userId}`;
    }
  } catch (error) {
    console.error("获取学年缓存key失败:", error);
  }
  return SCHOOL_YEAR_STORAGE_KEY;
};

const readSchoolYearFromStorage = (key: string): ISchoolYear | null => {
  try {
    const storedValue = localStorage.getItem(key);
    if (storedValue) {
      return JSON.parse(storedValue);
    }
  } catch (error) {
    console.error("解析学年缓存失败:", error);
  }
  return null;
};

/**
 * 保存学年信息到本地存储
 */
export const saveSchoolYearToStorage = (schoolYear: ISchoolYear): void => {
  try {
    const storageKey = getScopedStorageKey();
    localStorage.setItem(storageKey, JSON.stringify(schoolYear));
    // 清理旧的通用 key，避免不同账号共用
    if (storageKey !== SCHOOL_YEAR_STORAGE_KEY) {
      localStorage.removeItem(SCHOOL_YEAR_STORAGE_KEY);
    }
  } catch (error) {
    console.error("保存学年信息到本地存储失败:", error);
  }
};

/**
 * 从本地存储获取学年信息
 */
export const getSchoolYearFromStorage = (): ISchoolYear | null => {
  const scopedKey = getScopedStorageKey();
  const scopedSchoolYear = readSchoolYearFromStorage(scopedKey);
  if (scopedSchoolYear && isValidSchoolYear(scopedSchoolYear)) {
    return scopedSchoolYear;
  }

  // 兼容旧版本通用 key
  if (scopedKey !== SCHOOL_YEAR_STORAGE_KEY) {
    const legacySchoolYear = readSchoolYearFromStorage(SCHOOL_YEAR_STORAGE_KEY);
    if (legacySchoolYear && isValidSchoolYear(legacySchoolYear)) {
      return legacySchoolYear;
    }
  }

  return null;
};

/**
 * 清除本地存储的学年信息
 */
export const clearSchoolYearFromStorage = (): void => {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && (key === SCHOOL_YEAR_STORAGE_KEY || key.startsWith(SCHOOL_YEAR_STORAGE_PREFIX))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch (error) {
    console.error("清除本地存储的学年信息失败:", error);
  }
};

/**
 * 检查学年信息是否有效
 */
export const isValidSchoolYear = (schoolYear: any): schoolYear is ISchoolYear => {
  return (
    schoolYear &&
    typeof schoolYear === "object" &&
    typeof schoolYear.id === "string" &&
    typeof schoolYear.name === "string" &&
    typeof schoolYear.schoolId === "string"
  );
};
